<template>
  <div class="command-center">
    <div class="glass-header">
      <div class="title-group">
        <h1 class="main-title">ROBOT COMMAND CENTER</h1>
        <p class="sub-title">PIX-Claw 机器人自动化指派系统 (DeRAS)</p>
      </div>
      <div class="system-status">
        <el-tag :type="mqttConnected ? 'success' : 'danger'" effect="dark">
          <i class="el-icon-connection"></i> {{ mqttConnected ? 'MQTT 已在线' : 'MQTT 离线' }}
        </el-tag>
      </div>
    </div>

    <div class="robot-grid" v-loading="loading">
      <div v-for="robot in robotList" :key="robot.id" class="robot-card-wrapper">
        <el-card :class="['robot-card', robot.status === 1 ? 'is-busy' : 'is-idle']">
          <div class="status-indicator">
            <span class="pulse"></span>
            {{ robot.status === 1 ? '工作中' : '待命' }}
          </div>
          <div class="robot-info">
            <h3 class="robot-name">{{ robot.name }}</h3>
            <div class="info-row">
              <span class="label">钱包余额</span>
              <span class="value">{{ robot.money }} Claw</span>
            </div>
            <div class="address-box">
              <i class="el-icon-key"></i> {{ shortenAddress(robot.walAddress) }}
            </div>
            <img src="../../images/车辆.png" alt="" style="width:100%">
          </div>
        </el-card>
      </div>
    </div>

    <el-dialog title="🚀 任务指派确认" v-model="assignDialogVisible" width="400px" append-to-body>
      <div class="request-content" v-if="pendingRequest">
        <p>车辆 <strong>{{ pendingRequest.robotName }}</strong> 收到任务请求：</p>
        <div class="task-box">
          <p>任务 ID: <span class="highlight">{{ pendingRequest.taskId }}</span></p>
          <p>时间: {{ formatDate(pendingRequest.timestamp) }}</p>
        </div>
        <p class="warn-text">点击确认后将向后端返回 CONFIRMED 回执。</p>
      </div>
      <span slot="footer">
        <el-button @click="assignDialogVisible = false" size="small">取消</el-button>
        <el-button type="primary" @click="confirmAssignment" :loading="confirming" size="small">确认指派</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from "axios";
const API_BASE = "http://localhost:8090";

export default {
  data() {
    return {
      robotList: [],
      loading: false,
      mqttClient: null,
      mqttConnected: false,
      assignDialogVisible: false,
      confirming: false,
      pendingRequest: { robotName: '', taskId: '', timestamp: null }
    };
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        const res = await axios.get(`${API_BASE}/user/getAllRoc`);
        if (res.data.code === 200) {
          this.robotList = res.data.data;
          this.$nextTick(() => { this.connectMqtt(); });
        }
      } catch (err) {
        this.$message.error("获取数据失败");
      } finally {
        this.loading = false;
      }
    },

    connectMqtt() {
      const mqttLib = window.mqtt;
      if (!mqttLib || (this.mqttClient && this.mqttClient.connected)) return;

      // 关键：对照后端 Java 代码的配置
      // SERVER_URI: wss://pixconsole.pixmoving.city:443/wss
      const brokerUrl = 'wss://pixconsole.pixmoving.city:443/wss';

      const options = {
        clientId: 'text111_frontend_' + Math.random().toString(16).slice(2, 8),
        username: 'admin',
        clean: true, // 对应 options.setCleanSession(true)
        reconnectPeriod: 3000, // 对应 options.setAutomaticReconnect(true)
        connectTimeout: 10000,
      };

      console.log("尝试建立 WSS 连接...", brokerUrl);

      try {
        this.mqttClient = mqttLib.connect(brokerUrl, options);

        this.mqttClient.on('connect', () => {
          this.mqttConnected = true;
          console.log('✅ 前端 MQTT 已连接成功');
          // 订阅所有机器人的主题
          this.robotList.forEach(robot => {
            this.mqttClient.subscribe(`/pix/claw/${robot.name}`);
          });
        });

        this.mqttClient.on('message', (topic, message) => {
          console.log(`收到指令 [${topic}]:`, message.toString());
          try {
            const payload = JSON.parse(message.toString());
            // 匹配后端 addRocTask 发出的 action: ASSIGN
            if (payload.action === 'ASSIGN') {
              this.pendingRequest = {
                robotName: topic.split('/').pop(),
                taskId: payload.taskId,
                timestamp: payload.timestamp
              };
              this.assignDialogVisible = true;
            }
          } catch (e) {
            console.error("解析消息失败");
          }
        });

        this.mqttClient.on('error', (err) => {
          console.error("MQTT 连接错误:", err);
          this.mqttConnected = false;
        });

        this.mqttClient.on('close', () => {
          this.mqttConnected = false;
        });
      } catch (e) {
        console.error("MQTT 初始化失败", e);
      }
    },

    // 对应后端：mqttClient.subscribe(topic, (t, message) -> ... CONFIRMED ...)
    async confirmAssignment() {
      if (!this.mqttClient || !this.mqttConnected) {
        this.$message.error("MQTT 未连接");
        return;
      }

      this.confirming = true;
      const topic = `/pix/claw/${this.pendingRequest.robotName}`;
      const response = {
        taskId: String(this.pendingRequest.taskId),
        status: "CONFIRMED"
      };

      try {
        // 发布回执消息，使后端 CompletableFuture 能够完成
        this.mqttClient.publish(topic, JSON.stringify(response), { qos: 1 });
        this.$message.success("指派回执已发送，等待系统同步");
        
        setTimeout(() => {
          this.assignDialogVisible = false;
          this.confirming = false;
          this.init(); // 刷新机器人状态
        }, 1500);
      } catch (err) {
        this.$message.error("回执发送失败");
        this.confirming = false;
      }
    },

    shortenAddress(addr) {
      return addr ? `${addr.slice(0, 8)}...${addr.slice(-8)}` : 'N/A';
    },
    formatDate(ts) {
      return ts ? new Date(ts).toLocaleString() : '刚刚';
    }
  },
  beforeUnmount() {
    if (this.mqttClient) this.mqttClient.end();
  }
};
</script>

<style scoped>
.command-center { padding: 20px; background: #f8fafc; min-height: 100vh; }
.glass-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #eee; padding-bottom: 15px; margin-bottom: 25px; }
.robot-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px; }
.robot-card { border-radius: 12px; transition: transform 0.2s; position: relative; }
.robot-card:hover { transform: translateY(-5px); }
.is-busy { border-left: 6px solid #E6A23C; }
.is-idle { border-left: 6px solid #67C23A; }
.status-indicator { position: absolute; top: 12px; right: 12px; font-size: 12px; }
.pulse { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #67C23A; margin-right: 4px; }
.is-busy .pulse { background: #E6A23C; animation: blink 1s infinite; }
@keyframes blink { 50% { opacity: 0; } }
.task-box { background: #f0f2f5; padding: 12px; border-radius: 6px; margin: 15px 0; }
.highlight { color: #409EFF; font-weight: bold; }
.warn-text { color: #909399; font-size: 12px; }
.address-box { font-size: 11px; color: #999; margin-top: 10px; word-break: break-all; }
</style>